import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, styled } from "@mui/material/styles";

import MDBox from "shared/ui/Box/MDBox";

export const EXPANDED_SIDEBAR_WIDTH = 260;
export const COLLAPSED_SIDEBAR_WIDTH = 72;

const AppSidebarRoot = styled(Drawer)(({ theme, ownerState }) => {
  const { transitions, breakpoints, palette } = theme;
  const { collapsed } = ownerState;

  const width = collapsed ? COLLAPSED_SIDEBAR_WIDTH : EXPANDED_SIDEBAR_WIDTH;

  return {
    "&.MuiDrawer-root": {
      position: "relative",
      width: "100%",
      height: "100vh",
      margin: 0,
      padding: 0,
      overflow: "hidden",
      zIndex: 1300,
    },

    "& .MuiPaper-root": {
      margin: 0,
      padding: 0,
      borderRadius: 0,
    },

    "& .MuiDrawer-paper": {
      position: "fixed",
      top: 0,
      left: 0,
      height: "100vh",
      width,
      backgroundColor: palette.dark.main,
      borderRight: `1px solid ${alpha(palette.white.main, 0.12)}`,
      boxShadow: "none",
      overflowX: "hidden",
      transition: transitions.create(["width", "background-color"], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.sharp,
      }),

      [breakpoints.down("xl")]: {
        width,
      },
    },
  };
});

function AppSidebarItem({ icon, name, active, collapsed }) {
  return (
    <ListItem disablePadding>
      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: 44,
          px: collapsed ? 0 : 1.3,
          borderRadius: "10px",
          cursor: "pointer",
          position: "relative",
          backgroundColor: active
            ? (theme) => alpha(theme.palette.primary.main, 0.34)
            : "transparent",
          transition: "all .15s ease",
          "&:hover": {
            backgroundColor: active
              ? (theme) => alpha(theme.palette.primary.main, 0.42)
              : (theme) => alpha(theme.palette.white.main, 0.07),
          },
          "&::after": {
            content: active ? '""' : "none",
            position: "absolute",
            right: collapsed ? 6 : 8,
            width: 3,
            height: 18,
            borderRadius: "999px",
            backgroundColor: "accent.main",
          },
        }}
      >
        <MDBox
          sx={{
            width: collapsed ? "100%" : 44,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "& svg": {
              width: 22,
              height: 22,
              stroke: (theme) => (active ? theme.palette.white.main : theme.palette.grey[400]),
              strokeWidth: 1.8,
            },
          }}
        >
          {icon}
        </MDBox>

        {!collapsed && (
          <ListItemText
            primary={name}
            sx={{
              ml: 1,
              "& span": {
                fontSize: "0.9rem",
                fontWeight: active ? 600 : 500,
                color: active ? "white.main" : "grey.400",
              },
            }}
          />
        )}
      </MDBox>
    </ListItem>
  );
}

AppSidebarItem.defaultProps = {
  active: false,
};

AppSidebarItem.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  collapsed: PropTypes.bool.isRequired,
};

function AppSidebar({ navigation, collapsed, onMouseEnter, onMouseLeave }) {
  const location = useLocation();
  const renderItems = (items) =>
    items.map(({ key, label, icon, to, activePath }) => (
      <NavLink key={key} to={to}>
        <AppSidebarItem
          name={label}
          icon={icon}
          collapsed={collapsed}
          active={
            activePath
              ? location.pathname === activePath || location.pathname.startsWith(`${activePath}/`)
              : location.pathname === to
          }
        />
      </NavLink>
    ));

  return (
    <AppSidebarRoot
      variant="permanent"
      ownerState={{ collapsed }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <MDBox
        sx={{
          height: 72,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.white.main, 0.1)}`,
        }}
      >
        <MDBox
          component="img"
          src={collapsed ? "/gymplanner-logo-mini.svg" : "/gymplanner-logo.svg"}
          alt="GymPlanner"
          sx={{
            height: collapsed ? 30 : 36,
            width: "auto",
            transition: "all 0.25s ease",
          }}
        />
      </MDBox>

      <List sx={{ px: 1.5, display: "flex", flexDirection: "column", gap: 0.4 }}>
        {renderItems(navigation.main)}
      </List>

      {navigation.account.length > 0 && (
        <MDBox sx={{ mt: "auto" }}>
          <Divider sx={{ borderColor: (theme) => alpha(theme.palette.white.main, 0.1) }} />
          <MDBox sx={{ px: 1.5, pb: 2 }}>
            <List>{renderItems(navigation.account)}</List>
          </MDBox>
        </MDBox>
      )}
    </AppSidebarRoot>
  );
}

AppSidebar.propTypes = {
  navigation: PropTypes.shape({
    main: PropTypes.arrayOf(PropTypes.object).isRequired,
    account: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  collapsed: PropTypes.bool.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
};

export default AppSidebar;
